import { PlusOutlined, SolutionOutlined } from "@ant-design/icons";
import { App, Button, Form, Input, Modal, Pagination, Spin, Table } from "antd";
import { useEffect, useState } from "react";
import {
	DataTableCard,
	EmptyState,
	PageHeader,
	RowActions,
	SearchInput,
} from "@/shared/components/ui";
import { useTableState } from "@/shared/hooks/useTableState";
import { isConflictError } from "@/shared/lib/api-error";
import { formatDate } from "@/shared/lib/format";
import {
	useCreatePosition,
	useDeletePosition,
	usePositionsList,
	useUpdatePosition,
} from "../hooks/usePositions";
import type { Position } from "../types";

interface PositionsMobileListProps {
	positions: Position[];
	isLoading: boolean;
	search: string;
	total: number;
	page: number;
	limit: number;
	onPageChange: (page: number, pageSize: number) => void;
	openCreate: () => void;
	openEdit: (position: Position) => void;
	onDelete: (id: string) => void;
	deleting: boolean;
}

function PositionsMobileList({
	positions,
	isLoading,
	search,
	total,
	page,
	limit,
	onPageChange,
	openCreate,
	openEdit,
	onDelete,
	deleting,
}: PositionsMobileListProps) {
	if (isLoading) {
		return (
			<div className="flex justify-center p-8">
				<Spin />
			</div>
		);
	}

	if (positions.length === 0) {
		return (
			<div className="p-4">
				<EmptyState
					title={search ? "Hech narsa topilmadi" : "Lavozimlar yo'q"}
					description={
						search
							? "Boshqa nom bilan qidirib ko'ring."
							: "Birinchi lavozimni qo'shing — keyin uni xodimlarga bog'lash mumkin."
					}
					icon={<SolutionOutlined />}
					action={
						search ? null : (
							<Button icon={<PlusOutlined />} onClick={openCreate}>
								Lavozim qo'shish
							</Button>
						)
					}
				/>
			</div>
		);
	}

	return (
		<div>
			<div className="divide-y divide-[var(--c-border-subtle)]">
				{positions.map((position) => (
					<div key={position.id} className="flex items-center gap-3 p-3.5">
						<div className="min-w-0 flex-1">
							<div className="truncate text-sm font-medium text-[var(--c-text)]">
								{position.name}
							</div>
							<div className="numeric mt-0.5 text-xs text-[var(--c-text-muted)]">
								{formatDate(position.createdAt)}
							</div>
						</div>

						<RowActions
							alwaysVisible
							onEdit={() => openEdit(position)}
							onDelete={() => onDelete(position.id)}
							deleteTitle="Lavozim o'chirilsinmi?"
							deleting={deleting}
						/>
					</div>
				))}
			</div>

			{total > limit && (
				<div className="flex justify-center border-t border-[var(--c-border-subtle)] p-3">
					<Pagination
						simple
						current={page}
						pageSize={limit}
						total={total}
						onChange={onPageChange}
					/>
				</div>
			)}
		</div>
	);
}

export function PositionsPage() {
	const { message } = App.useApp();
	const { page, limit, search, onSearch, onPageChange } = useTableState();
	const { data, isLoading, isFetching } = usePositionsList({
		page,
		limit,
		name: search || undefined,
	});

	const [form] = Form.useForm<{ name: string }>();
	const [modalOpen, setModalOpen] = useState(false);
	const [editing, setEditing] = useState<Position | null>(null);

	const createMutation = useCreatePosition();
	const updateMutation = useUpdatePosition();
	const deleteMutation = useDeletePosition();

	useEffect(() => {
		if (modalOpen) {
			form.setFieldsValue({ name: editing?.name ?? "" });
		}
	}, [modalOpen, editing, form]);

	const openCreate = () => {
		setEditing(null);
		setModalOpen(true);
	};

	const openEdit = (position: Position) => {
		setEditing(position);
		setModalOpen(true);
	};

	const handleSubmit = async () => {
		const values = await form.validateFields();

		if (editing) {
			updateMutation.mutate(
				{ id: editing.id, data: values },
				{ onSuccess: () => setModalOpen(false) }
			);
		} else {
			createMutation.mutate(values, { onSuccess: () => setModalOpen(false) });
		}
	};

	const handleDelete = (id: string) => {
		deleteMutation.mutate(id, {
			onError: (error) => {
				message.error(
					isConflictError(error)
						? "O'chirib bo'lmadi: lavozim ishlatilmoqda"
						: "O'chirishda xatolik yuz berdi"
				);
			},
		});
	};

	const total = data?.meta.total ?? 0;
	const positions = data?.items ?? [];
	const isDataLoading = isLoading || isFetching;

	return (
		<div>
			<PageHeader
				title="Lavozimlar"
				description="Xodimlar lavozimlari ro'yxati"
				actions={
					<Button
						type="primary"
						icon={<PlusOutlined />}
						onClick={openCreate}
						className="w-full sm:w-auto"
					>
						Lavozim qo'shish
					</Button>
				}
			/>

			<DataTableCard
				toolbar={
					<SearchInput
						placeholder="Nomi bo'yicha qidirish…"
						onSearch={onSearch}
						className="w-full sm:w-64"
					/>
				}
				toolbarExtra={total ? `${total} ta yozuv` : null}
			>
				{/* Desktop table */}
				<div className="hidden sm:block">
					<Table<Position>
						rowKey="id"
						loading={isDataLoading}
						dataSource={positions}
						// max-content: only scrolls if a row genuinely overflows (e.g. a long
						// name), instead of forcing a fixed min-width that pushes the actions
						// column off-screen on narrow viewports.
						scroll={{ x: "max-content" }}
						locale={{
							emptyText: (
								<EmptyState
									title={search ? "Hech narsa topilmadi" : "Lavozimlar yo'q"}
									description={
										search
											? "Boshqa nom bilan qidirib ko'ring."
											: "Birinchi lavozimni qo'shing — keyin uni xodimlarga bog'lash mumkin."
									}
									icon={<SolutionOutlined />}
									action={
										search ? null : (
											<Button icon={<PlusOutlined />} onClick={openCreate}>
												Lavozim qo'shish
											</Button>
										)
									}
								/>
							),
						}}
						pagination={
							total > limit
								? {
										current: page,
										pageSize: limit,
										total,
										showSizeChanger: true,
										onChange: onPageChange,
										className: "!px-4",
									}
								: false
						}
						columns={[
							{
								title: "Nomi",
								dataIndex: "name",
								render: (value: string) => <span className="font-medium">{value}</span>,
							},
							{
								title: "Yaratilgan",
								dataIndex: "createdAt",
								width: 160,
								responsive: ["md"],
								render: (value: string) => (
									<span className="numeric text-[var(--c-text-secondary)]">
										{formatDate(value)}
									</span>
								),
							},
							{
								title: "",
								key: "actions",
								width: 88,
								align: "right",
								fixed: "right",
								render: (_, record) => (
									<RowActions
										onEdit={() => openEdit(record)}
										onDelete={() => handleDelete(record.id)}
										deleteTitle="Lavozim o'chirilsinmi?"
										deleting={deleteMutation.isPending}
									/>
								),
							},
						]}
					/>
				</div>

				{/* Mobile cards */}
				<div className="block sm:hidden">
					<PositionsMobileList
						positions={positions}
						isLoading={isDataLoading}
						search={search}
						total={total}
						page={page}
						limit={limit}
						onPageChange={onPageChange}
						openCreate={openCreate}
						openEdit={openEdit}
						onDelete={handleDelete}
						deleting={deleteMutation.isPending}
					/>
				</div>
			</DataTableCard>

			<Modal
				title={editing ? "Lavozimni tahrirlash" : "Yangi lavozim"}
				open={modalOpen}
				onCancel={() => setModalOpen(false)}
				onOk={handleSubmit}
				okText={editing ? "Saqlash" : "Qo'shish"}
				cancelText="Bekor qilish"
				confirmLoading={createMutation.isPending || updateMutation.isPending}
				destroyOnHidden
			>
				<Form form={form} layout="vertical" className="mt-4" requiredMark={false}>
					<Form.Item
						name="name"
						label="Nomi"
						rules={[{ required: true, message: "Nomi kiritilishi shart" }]}
					>
						<Input placeholder="masalan: Menejer" autoFocus />
					</Form.Item>
				</Form>
			</Modal>
		</div>
	);
}
